/**
 * Validação mínima antes de enfileirar eventos.
 */

import type { SendEventParams } from '../utils/sendEvent';
import { shouldTrack, getConsent } from './consent';

const MAX_EVENT_DATA_BYTES = 32_000;
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const CPF_RE = /\d{3}\.?\d{3}\.?\d{3}-?\d{2}/g;

export interface ValidateEventResult {
  valid: boolean;
  error?: string;
  redactedData?: SendEventParams['eventData'];
}

function redactPii(data: SendEventParams['eventData']): SendEventParams['eventData'] {
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return value.replace(EMAIL_RE, '[email]').replace(CPF_RE, '[cpf]');
    }
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      const o: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) o[k] = walk(v);
      return o;
    }
    return value;
  };
  return walk(data) as SendEventParams['eventData'];
}

export function validateEventParams(params: SendEventParams): ValidateEventResult {
  if (!shouldTrack()) {
    return { valid: false, error: 'consent_disabled' };
  }
  if (!params.config?.apiEndpoint || !params.config?.pixel) {
    return { valid: false, error: 'invalid_config' };
  }
  if (!params.visitorId || !params.sessionId) {
    return { valid: false, error: 'missing_identity' };
  }
  let serialized = '';
  try {
    serialized = JSON.stringify(params.eventData ?? {});
  } catch {
    return { valid: false, error: 'non_serializable_event_data' };
  }
  if (new TextEncoder().encode(serialized).length > MAX_EVENT_DATA_BYTES) {
    return { valid: false, error: 'event_data_too_large' };
  }
  const consent = getConsent();
  const needsRedact = !consent.analytics || !consent.marketing;
  return {
    valid: true,
    redactedData: needsRedact
      ? redactPii(params.eventData ?? {})
      : (params.eventData ?? {}),
  };
}
